<script lang="ts" setup>
import { inject, ref, computed } from 'vue'
import type { CustomerDbRecord } from '~/types/db/customer'
import type { Check } from '~/types/checks'

const { t } = useNuxtApp().$i18n
const dialogRef = inject('dialogRef') as any

const openChecks: Check[] = dialogRef.value.data?.openChecks || []

const selectedCustomer = ref<CustomerDbRecord | null>(null)
const customerEditMode = ref(false)

const existingCustomerCheck = computed(() => {
  if (!selectedCustomer.value?.id) return null
  return openChecks.find(
    (check: Check) => check.customer_id === selectedCustomer.value!.id && check.status === 'open',
  ) || null
})

function onCustomerSelect(customer: CustomerDbRecord) {
  selectedCustomer.value = customer
  customerEditMode.value = false
}

function onCustomerClear() {
  selectedCustomer.value = null
  customerEditMode.value = false
}

function confirm() {
  const customerData: Record<string, any> = {}

  if (selectedCustomer.value?.id) {
    customerData.customer_id = selectedCustomer.value.id
  }

  const name = selectedCustomer.value?.name
  const phone = selectedCustomer.value?.phone

  if (name) customerData.customer_name = name
  if (phone) customerData.customer_phone = phone

  dialogRef.value.close({
    confirmed: true,
    customerData: Object.keys(customerData).length ? customerData : undefined,
  })
}

function cancel() {
  dialogRef.value.close()
}
</script>

<template>
  <div class="flex flex-col gap-4 p-2">
    <p class="text-sm text-gray-500">
      {{ t('Client') }} ({{ t('Optional') }})
    </p>

    <!-- Selected customer read-only card -->
    <div
      v-if="selectedCustomer && !customerEditMode"
      class="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
    >
      <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
        <i class="pi pi-user text-green-600" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-gray-800 truncate">
          {{ selectedCustomer.name }}
        </p>
        <p
          v-if="selectedCustomer.phone"
          class="text-xs text-gray-500 truncate"
        >
          <i class="pi pi-phone text-[10px]" /> {{ selectedCustomer.phone }}
        </p>
        <p
          v-if="selectedCustomer.email"
          class="text-xs text-gray-500 truncate"
        >
          <i class="pi pi-envelope text-[10px]" /> {{ selectedCustomer.email }}
        </p>
        <p
          v-if="selectedCustomer.comments"
          class="text-xs text-gray-400 truncate italic"
        >
          {{ selectedCustomer.comments }}
        </p>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <button
          class="w-8 h-8 rounded-full flex items-center justify-center bg-transparent hover:bg-gray-200 border-0 cursor-pointer transition-colors"
          :aria-label="t('Edit')"
          @click="customerEditMode = true"
        >
          <i class="pi pi-pencil text-sm text-gray-500" />
        </button>
        <button
          class="w-8 h-8 rounded-full flex items-center justify-center bg-transparent hover:bg-gray-200 border-0 cursor-pointer transition-colors"
          :aria-label="t('Clear client')"
          @click="onCustomerClear"
        >
          <i class="pi pi-times text-sm text-gray-500" />
        </button>
      </div>
    </div>

    <!-- Customer edit mode -->
    <SharedCustomerPicker
      v-if="selectedCustomer && customerEditMode"
      v-model="selectedCustomer"
      :show-form="false"
      @select="onCustomerSelect"
      @clear="onCustomerClear"
    />

    <!-- Customer picker (no customer selected) -->
    <div v-if="!selectedCustomer">
      <SharedCustomerPicker
        v-model="selectedCustomer"
        :show-form="false"
        @select="onCustomerSelect"
        @clear="onCustomerClear"
      />
    </div>

    <!-- Duplicate customer warning -->
    <div
      v-if="existingCustomerCheck"
      class="flex items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-700 text-sm"
    >
      <i class="pi pi-exclamation-triangle" />
      <span>{{ t('{name} already has an open check', { name: selectedCustomer!.name }) }}</span>
    </div>

    <!-- Actions -->
    <div class="flex justify-end gap-2 pt-2">
      <Button
        :label="t('Cancel')"
        severity="secondary"
        outlined
        @click="cancel"
      />
      <Button
        :label="selectedCustomer ? t('Use {name} in new check', { name: selectedCustomer.name }) : t('Open Check')"
        severity="success"
        icon="pi pi-check"
        :disabled="!!existingCustomerCheck"
        @click="confirm"
      />
    </div>
  </div>
</template>
